import React from "react";

import { Tooltip, styled } from "@mui/material";

import TimelineItem from "./TimelineItem";

const TimelineRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  position: "relative",
  minHeight: "30px",
  marginRight: "12px",
  paddingTop: "6px",
  paddingBottom: "44px",
  alignSelf: "flex-start",
});

const TimelineWrapper = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  position: "relative",
  flex: 1,
});

const AxisLine = styled("div")({
  position: "absolute",
  top: "50%",
  left: "0",
  right: "0",
  height: "2px",
  backgroundColor: "#1976d2",
  transform: "translateY(-50%)",
});

const TimelineSection = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "0",
  position: "relative",
  zIndex: 1,
});

const DividerWrapper = styled("div")({
  position: "relative",
  width: "10px",
  flexShrink: 0,
  left: "-5px",
});

const PeriodDivider = styled("div")({
  width: "1px",
  backgroundColor: "#1976d2",
  flexShrink: 0,
  position: "absolute",
  top: "-30px",
  bottom: "-80px",
  left: "-10px",
  zIndex: 10,
});

/**
 * Desktop timeline renderer (axis + observation/projection ticks + divider).
 * Stateless: receives prepared props and calls onPickOriginalIndex.
 */
function DesktopTimeline({
  sliderItems,
  originalItems,
  displaySelected,
  isChangeMode,
  onPickOriginalIndex,
}) {
  const observationItems = (sliderItems || []).filter(
    (item) => !item.projection
  );

  // Only show the "deactivate change mode" tooltip when change mode is actively
  // blocking observations — not when rasters are simply unavailable (different cause).
  const showChangeModeTooltip =
    isChangeMode &&
    observationItems.length > 0 &&
    observationItems.some((item) => item.used);

  return (
    <TimelineRow id="klimaSliderTimeline">
      <TimelineWrapper>
        <AxisLine />

        {/* Beobachtungsdaten */}
        <div style={{ position: "relative" }}>
          <TimelineSection>
            {observationItems.map((item) => {
              const originalIndex = originalItems.indexOf(item);
              const { label = item, used } = item;
              // observation availability: disabled when change-mode active
              const available = used && !isChangeMode;
              const isSelected = displaySelected === originalIndex;

              return (
                <TimelineItem
                  key={`beob-${originalIndex}`}
                  originalIndex={originalIndex}
                  labelHtml={label}
                  selected={isSelected}
                  available={available}
                  projection={false}
                  onPick={onPickOriginalIndex}
                />
              );
            })}
          </TimelineSection>

          {showChangeModeTooltip && (
            <Tooltip
              title='Deaktivieren Sie "Änderung zu 1971–2000", um Beobachtungsdaten wieder auswählen zu können.'
              arrow
              placement="top"
            >
              <span
                tabIndex={0}
                style={{
                  position: "absolute",
                  inset: "-1px 10px -34px 0px",
                  zIndex: 10,
                  pointerEvents: "auto",
                  background: "transparent",
                }}
              />
            </Tooltip>
          )}
        </div>

        {/* Divider that cuts through the box */}
        <DividerWrapper>
          <PeriodDivider />
        </DividerWrapper>

        {/* Projektionsdaten */}
        <TimelineSection>
          {(sliderItems || [])
            .filter((item) => item.projection)
            .map((item) => {
              const originalIndex = originalItems.indexOf(item);
              const { label = item, used } = item;
              const isSelected = displaySelected === originalIndex;
              const available = used;

              return (
                <TimelineItem
                  key={`proj-${originalIndex}`}
                  originalIndex={originalIndex}
                  labelHtml={label}
                  selected={isSelected}
                  available={available}
                  projection={true}
                  onPick={onPickOriginalIndex}
                />
              );
            })}
        </TimelineSection>
      </TimelineWrapper>
    </TimelineRow>
  );
}

export default DesktopTimeline;
